// There is deliberately no card-status enum: status is derived server-side and rendered by `name`.
// What the pages branch on is the phase, derived from the uploads — `draft` below is the one exception.
export const RATE_CARD_SIDES = {
  PAY: 'pay',
  BILL: 'bill',
} as const;

export type RateCardSideId = (typeof RATE_CARD_SIDES)[keyof typeof RATE_CARD_SIDES];

export const RATE_CARD_SIDE_IDS = [RATE_CARD_SIDES.PAY, RATE_CARD_SIDES.BILL] as const;

// Mirrors the UploaderItem states in @jjj/move — a UI state, not a server one.
export const RATE_CARD_UPLOAD_STATES = {
  UPLOADING: 'uploading',
  ERROR: 'error',
  SUCCESS: 'success',
} as const;

export type RateCardUploadState = (typeof RATE_CARD_UPLOAD_STATES)[keyof typeof RATE_CARD_UPLOAD_STATES];

export const RATE_CARD_UPLOAD_STATUSES = {
  UPLOADED: 'uploaded',
  PROCESSING: 'processing',
  PROCESSED: 'processed',
  PROCESSED_WITH_ERRORS: 'processed_with_errors',
} as const;

/**
 * The card status the delete endpoint accepts. The server calls the unpublished case `draft`, so
 * testing for it asks exactly what that endpoint's own precondition asks.
 */
export const RATE_CARD_DRAFT_STATUS = 'draft';

/**
 * The one action a list row advertises. It answers the status half of the question only — the card
 * page, which gets no such payload, asks the status itself and both pages check the permission.
 */
export const RATE_CARD_DELETE_ACTION = 'delete';

/** `uploaded` and `processing` are the two the importer still moves out of, so they drive polling. */
export const RATE_CARD_PENDING_UPLOAD_STATUSES: readonly string[] = [
  RATE_CARD_UPLOAD_STATUSES.UPLOADED,
  RATE_CARD_UPLOAD_STATUSES.PROCESSING,
];

export const RATE_CARD_FINDING_SEVERITIES = {
  BLOCKER: 'blocker',
  CONFIRMATION: 'confirmation',
  WARNING: 'warning',
} as const;

export type RateCardFindingSeverity = (typeof RATE_CARD_FINDING_SEVERITIES)[keyof typeof RATE_CARD_FINDING_SEVERITIES];

/**
 * The two blockers that resolve on their own, so they get a re-check rather than a correction. Spelled
 * out because rule names are per-check on the server: a rename degrades this to "correct your file".
 */
export const RATE_CARD_WAITING_RULES: readonly string[] = ['upload_not_processed', 'rows_not_resolved'];

// The upload form accepts CSV only, at 15 MB, and detects a spoofed content type.
export const RATE_CARDS_UPLOAD_ACCEPT = '.csv';

export const RATE_CARDS_UPLOAD_MAX_BYTES = 15 * 1024 * 1024;

/** The only content type the upload form accepts alongside `text/plain`. */
export const CSV_CONTENT_TYPE = 'text/csv';

export const RATE_CARD_WIZARD_STEPS = {
  DETAILS: 0,
  IMPORT: 1,
} as const;

export const RATE_CARD_REVIEW_STEPS = {
  RATE_CARDS: 0,
  REVIEW: 1,
} as const;

// The export endpoint streams CSV and takes no format parameter, so the side is all that varies.
export const RATE_CARD_EXPORT_OPTIONS = [
  { id: 'pay_csv', side: RATE_CARD_SIDES.PAY },
  { id: 'bill_csv', side: RATE_CARD_SIDES.BILL },
] as const;

export const RATE_CARDS_EDITOR_PAGE_MAX_WIDTH = 864;

export const RATE_CARDS_EDITOR_OMNIBAR_TITLE_FONT_SIZE = 18;

export const RATE_CARDS_LIST_PAGE_SIZE = 10;

/** The columns the list grid can sort by (see `columns.ts`); the response says which of them the endpoint will. */
export const RATE_CARDS_SORT_COLUMNS = ['name', 'status', 'effective_from'] as const;

/** Written to the URL on mount and sent when the query names no usable sort: the endpoint's own default is `name`. */
export const RATE_CARDS_DEFAULT_SORT = { column: 'status', order: 'ascend' } as const;

export const RATE_CARDS_LINES_PAGE_SIZE = 25;

/** Also a lines-grid column key, not only a filter name. */
export const RATE_CARD_PARITY_KEY = 'parity_status';

/**
 * The filter names this UI will forward; which of them a side has is the response's to say. Literals
 * rather than `string[]`, so the grid's label map can be made total over them.
 */
export const RATE_CARD_LINE_FILTER_KEYS = [
  'position_id',
  'job_type_id',
  'location_id',
  'agency_id',
  RATE_CARD_PARITY_KEY,
] as const;

export type RateCardLineFilterKey = (typeof RATE_CARD_LINE_FILTER_KEYS)[number];

/** How often the detail page re-reads a card whose import is still being processed. */
export const RATE_CARD_POLL_INTERVAL_MS = 5000;

/** Roughly fifteen minutes. An importer that dies leaves an upload `processing` forever. */
export const RATE_CARD_POLL_MAX_ATTEMPTS = 180;

/** A blip is worth riding out; a sustained outage is not worth asking about every five seconds. */
export const RATE_CARD_POLL_MAX_FAILURES = 3;

export const RATE_CARD_HTTP = {
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE: 422,
} as const;
